import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, equalBytes, fromBase64Url, hexToBytes, utf8 } from './bytes.ts';
import { canonicalize, sha256Hex } from './canon.ts';
import { cborDecode, cborEncode, type CborValue } from './cbor.ts';
import { MAX_REGISTER_DATA_BYTES } from './record.ts';

/**
 * The company layer (SPEC.md §8): a company's Concordium account binds its
 * domains and NetID signing keys.
 *
 *  - the BINDING DOCUMENT (off chain, y-canon-v2 JSON) names the account, its
 *    identity provider, the domains and the keys; its SHA-256 is the
 *    challenge of the company's zero-knowledge identity proof;
 *  - the BINDING RECORD (on chain, a RegisterData of at most 256 bytes, sent
 *    from the company account) carries that hash, the domains, the key
 *    fingerprints, the validity and the proof's hash;
 *  - a REVOCATION RECORD (also from the company account) withdraws a binding,
 *    or some of its keys.
 */
export type BindingKey = {
  kid: string;
  alg: 'Ed25519';
  /** Raw 32-byte Ed25519 public key, base64url. */
  pub: string;
  /** RFC 3339 UTC, whole seconds. */
  not_before: string;
  not_after: string;
};

export type BindingDocument = {
  v: 1;
  company: { network: 'mainnet' | 'testnet'; account: string; idp: number };
  domains: string[];
  keys: BindingKey[];
  issued_at: string;
  /** The binding is void after this (no later than the identity credential's validTo). */
  not_after: string;
};

export const BINDING_RECORD_TYPE = 'netid-binding';
export const REVOCATION_RECORD_TYPE = 'netid-revoke';
export const BINDING_RECORD_VERSION = 1;

const HOST = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
const RFC3339 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;

export function bindingProblems(doc: BindingDocument): string[] {
  const out: string[] = [];
  if (!doc || typeof doc !== 'object') return ['not an object'];
  if (doc.v !== 1) out.push('v != 1');
  const c = doc.company;
  if (!c || !['mainnet', 'testnet'].includes(c.network) || typeof c.account !== 'string' || !/^[1-9A-HJ-NP-Za-km-z]{50}$/.test(c.account)) out.push('company.account');
  if (!c || !Number.isSafeInteger(c.idp) || c.idp < 0) out.push('company.idp');
  if (!Array.isArray(doc.domains) || doc.domains.length < 1 || doc.domains.length > 16) out.push('domains');
  else {
    for (const d of doc.domains) if (typeof d !== 'string' || !HOST.test(d)) out.push(`domain ${d}`);
    if (new Set(doc.domains).size !== doc.domains.length) out.push('duplicate domains');
  }
  if (!Array.isArray(doc.keys) || doc.keys.length < 1 || doc.keys.length > 4) out.push('keys (1–4)');
  else {
    for (const k of doc.keys) {
      if (!k || typeof k.kid !== 'string' || !k.kid || k.alg !== 'Ed25519') out.push(`key ${k?.kid}`);
      else {
        try {
          if (fromBase64Url(k.pub).length !== 32) out.push(`key ${k.kid}: pub is not 32 bytes`);
        } catch {
          out.push(`key ${k.kid}: pub is not base64url`);
        }
        if (!RFC3339.test(k.not_before) || !RFC3339.test(k.not_after) || k.not_after <= k.not_before) out.push(`key ${k.kid}: validity`);
      }
    }
    if (new Set(doc.keys.map((k) => k?.kid)).size !== doc.keys.length) out.push('duplicate kids');
  }
  if (!RFC3339.test(doc.issued_at ?? '')) out.push('issued_at');
  if (!RFC3339.test(doc.not_after ?? '') || doc.not_after <= doc.issued_at) out.push('not_after');
  return out;
}

/** The canonical bytes and their SHA-256 (hex): the proof challenge and the record's `b`. */
export function bindingHash(doc: BindingDocument): { canonical: string; sha256: string } {
  const problems = bindingProblems(doc);
  if (problems.length) throw new Error(`invalid binding document: ${problems.join('; ')}`);
  const canonical = canonicalize(doc);
  return { canonical, sha256: sha256Hex(canonical) };
}

/** SHA-256 of a key's raw 32 public-key bytes. */
export function keyFingerprint(pubBase64Url: string): Uint8Array {
  return sha256(fromBase64Url(pubBase64Url));
}

/** SHA-256 of the y-canon-v2 JSON array of domains (used when the list is too long for the record). */
export function domainsHash(domains: string[]): Uint8Array {
  return sha256(utf8(canonicalize(domains)));
}

const unix = (iso: string) => Math.floor(Date.parse(iso) / 1000);

export type BindingRecord = {
  type: 'binding';
  v: number;
  /** SHA-256 of the binding document. */
  b: string;
  /** SHA-256 of the identity proof JSON, when one was made first. */
  p: string | null;
  /** The domains themselves, or (when they do not fit) only their hash and count. */
  domains: string[] | null;
  domainsHash: string | null;
  domainCount: number;
  /** Key fingerprints (SHA-256 of the raw public key), hex. */
  keys: string[];
  /** Validity, Unix seconds. */
  notBefore: number;
  notAfter: number;
};

export type RevocationRecord = {
  type: 'revocation';
  v: number;
  /** The binding document hash that is withdrawn. */
  b: string;
  /** Only these key fingerprints are withdrawn; null = the whole binding. */
  keys: string[] | null;
  at: number;
};

export type NetIdRecord = BindingRecord | RevocationRecord;

/**
 * The on-chain binding record: deterministic CBOR,
 *   { t:"netid-binding", v:1, b:<32>, p:<32>?, d:[domains] | dh:<32>+dn:n, k:[<32>…], nb, na }
 * Falls back to the domains' hash when the list does not fit in 256 bytes.
 */
export function encodeBindingRecord(doc: BindingDocument, proofSha256: string | null): Uint8Array {
  const { sha256: b } = bindingHash(doc);
  if (proofSha256 !== null && !/^[0-9a-f]{64}$/.test(proofSha256)) throw new Error('proof hash is not 64 hex characters');
  const base: Record<string, CborValue> = {
    t: BINDING_RECORD_TYPE,
    v: BINDING_RECORD_VERSION,
    b: hexToBytes(b),
    k: doc.keys.map((k) => keyFingerprint(k.pub)),
    nb: unix(doc.issued_at),
    na: unix(doc.not_after),
  };
  if (proofSha256) base.p = hexToBytes(proofSha256);
  const withList = cborEncode({ ...base, d: doc.domains });
  if (withList.length <= MAX_REGISTER_DATA_BYTES) return withList;
  const withHash = cborEncode({ ...base, dh: domainsHash(doc.domains), dn: doc.domains.length });
  if (withHash.length <= MAX_REGISTER_DATA_BYTES) return withHash;
  throw new Error(`binding record is ${withHash.length} bytes (max ${MAX_REGISTER_DATA_BYTES}); use fewer keys`);
}

/** `{ t:"netid-revoke", v:1, b:<32>, at, k:[<32>…]? }` */
export function encodeRevocationRecord(bindingSha256: string, at: Date, keyFingerprints?: Uint8Array[]): Uint8Array {
  const rec: Record<string, CborValue> = { t: REVOCATION_RECORD_TYPE, v: 1, b: hexToBytes(bindingSha256), at: Math.floor(at.getTime() / 1000) };
  if (keyFingerprints?.length) rec.k = keyFingerprints;
  const out = cborEncode(rec);
  if (out.length > MAX_REGISTER_DATA_BYTES) throw new Error('revocation record too large');
  return out;
}

const isBytes = (v: unknown, n?: number): v is Uint8Array => v instanceof Uint8Array && (n === undefined || v.length === n);
const hex32 = (v: unknown, what: string): string => {
  if (!isBytes(v, 32)) throw new Error(`NetID record: ${what} is not 32 bytes`);
  return bytesToHex(v);
};

/** Decodes a NetID binding or revocation record; throws on anything else (e.g. a y.dk article record). */
export function decodeNetIdRecord(data: Uint8Array): NetIdRecord {
  const v = cborDecode(data);
  if (!v || typeof v !== 'object' || Array.isArray(v) || v instanceof Uint8Array) throw new Error('NetID record: not a map');
  const m = v as Record<string, CborValue>;
  if (m.v !== 1) throw new Error(`NetID record: unsupported version ${String(m.v)}`);
  if (m.t === BINDING_RECORD_TYPE) {
    const allowed = new Set(['t', 'v', 'b', 'p', 'd', 'dh', 'dn', 'k', 'nb', 'na']);
    for (const k of Object.keys(m)) if (!allowed.has(k)) throw new Error(`NetID record: unknown field ${k}`);
    if (!Array.isArray(m.k) || m.k.length < 1) throw new Error('NetID record: no keys');
    if (typeof m.nb !== 'number' || typeof m.na !== 'number' || m.na <= m.nb) throw new Error('NetID record: validity');
    let domains: string[] | null = null;
    let dh: string | null = null;
    let dn = 0;
    if (m.d !== undefined) {
      if (!Array.isArray(m.d) || !m.d.every((x) => typeof x === 'string')) throw new Error('NetID record: d');
      domains = m.d as string[];
      dn = domains.length;
    } else {
      dh = hex32(m.dh, 'dh');
      if (typeof m.dn !== 'number' || m.dn < 1) throw new Error('NetID record: dn');
      dn = m.dn;
    }
    return {
      type: 'binding',
      v: 1,
      b: hex32(m.b, 'b'),
      p: m.p === undefined ? null : hex32(m.p, 'p'),
      domains,
      domainsHash: dh,
      domainCount: dn,
      keys: m.k.map((x, i) => hex32(x, `k[${i}]`)),
      notBefore: m.nb,
      notAfter: m.na,
    };
  }
  if (m.t === REVOCATION_RECORD_TYPE) {
    const allowed = new Set(['t', 'v', 'b', 'at', 'k']);
    for (const k of Object.keys(m)) if (!allowed.has(k)) throw new Error(`NetID record: unknown field ${k}`);
    if (typeof m.at !== 'number') throw new Error('NetID record: at');
    let keys: string[] | null = null;
    if (m.k !== undefined) {
      if (!Array.isArray(m.k)) throw new Error('NetID record: k');
      keys = m.k.map((x, i) => hex32(x, `k[${i}]`));
    }
    return { type: 'revocation', v: 1, b: hex32(m.b, 'b'), keys, at: m.at };
  }
  throw new Error(`NetID record: unknown type ${String(m.t)}`);
}

/** Every way an on-chain binding record differs from the binding document (and the proof). Empty = they agree. */
export function bindingRecordDiffs(rec: BindingRecord, doc: BindingDocument, proofSha256: string | null): string[] {
  const out: string[] = [];
  const { sha256: b } = bindingHash(doc);
  if (rec.b !== b) out.push(`the record names binding ${rec.b.slice(0, 12)}…, the document hashes to ${b.slice(0, 12)}…`);
  if (rec.domains) {
    if (canonicalize(rec.domains) !== canonicalize(doc.domains)) out.push(`domains on chain [${rec.domains.join(', ')}] ≠ [${doc.domains.join(', ')}]`);
  } else if (rec.domainsHash !== bytesToHex(domainsHash(doc.domains)) || rec.domainCount !== doc.domains.length) {
    out.push('the domains hash on chain does not match the document');
  }
  const fps = doc.keys.map((k) => bytesToHex(keyFingerprint(k.pub)));
  if (canonicalize(rec.keys) !== canonicalize(fps)) out.push('the key fingerprints on chain do not match the document');
  if (rec.notBefore !== unix(doc.issued_at) || rec.notAfter !== unix(doc.not_after)) out.push('the validity on chain does not match the document');
  if (proofSha256 && rec.p && rec.p !== proofSha256) out.push(`the record names proof ${rec.p.slice(0, 12)}…, the published proof hashes to ${proofSha256.slice(0, 12)}…`);
  if (proofSha256 && !rec.p) out.push('the record names no proof');
  return out;
}

/** Is this public key one of the binding's keys, valid at `now`? */
export function bindingKeyFor(doc: BindingDocument, kid: string, publicKey: Uint8Array, now: number): { key: BindingKey } | { error: string } {
  const k = doc.keys.find((x) => x.kid === kid);
  if (!k) return { error: `key ${kid} is not in the binding` };
  if (!equalBytes(fromBase64Url(k.pub), publicKey)) return { error: `key ${kid} in the binding has a different public key` };
  if (now < Date.parse(k.not_before)) return { error: `key ${kid} is bound only from ${k.not_before}` };
  if (now > Date.parse(k.not_after)) return { error: `key ${kid}'s binding ended ${k.not_after}` };
  return { key: k };
}

/** Is `host` one of the bound domains, or a subdomain of one? */
export function hostIsBound(doc: BindingDocument, host: string): boolean {
  const h = host.toLowerCase().replace(/\.$/, '');
  return doc.domains.some((d) => h === d || h.endsWith(`.${d}`));
}

// ---- DNS TXT `_netid.<domain>` --------------------------------------------

export function dnsTxtValue(account: string, bindingSha256: string, txHash?: string | null): string {
  return `v=netid1; acct=${account}; bind=${bindingSha256}${txHash ? `; tx=${txHash}` : ''}`;
}

export function parseDnsTxt(txt: string): { v: string; acct?: string; bind?: string; tx?: string } | null {
  const clean = txt.replace(/^"|"$/g, '').replace(/"\s*"/g, '');
  const fields: Record<string, string> = {};
  for (const part of clean.split(';')) {
    const m = part.trim().match(/^([a-z0-9]+)=(.*)$/i);
    if (m) fields[m[1].toLowerCase()] = m[2].trim();
  }
  if (fields.v !== 'netid1') return null;
  return { v: fields.v, acct: fields.acct, bind: fields.bind, tx: fields.tx };
}
